"use client";

import React from "react";
import { stegaClean } from "@sanity/client/stega";

import type { PageRizAffiliateSchema } from "@/sanity/pages/PageRizAffiliateTemplate";
import { withAffiliateOfferDiscount } from "@/lib/affiliateDiscount";
import { withAffiliateOfferCopy } from "@/lib/affiliateOfferCopy";

import HeroSection from "../pageComponents/rizAffiliate/HeroSection";
import VipDealSection from "../pageComponents/rizAffiliate/VipDealSection";
import EdgeSection from "../pageComponents/rizAffiliate/EdgeSection";
import AboutSection from "../pageComponents/rizAffiliate/AboutSection";
import StepsSection from "../pageComponents/rizAffiliate/StepsSection";
import TradersSaySection from "../pageComponents/rizAffiliate/TradersSaySection";
import ComparisonSection from "../pageComponents/rizAffiliate/ComparisonSection";
import TestimonialsSection from "../pageComponents/rizAffiliate/TestimonialsSection";
import FAQSection from "../pageComponents/rizAffiliate/FAQSection";
import FinalCTASection from "../pageComponents/rizAffiliate/FinalCta";
import FooterSection from "../pageComponents/rizAffiliate/FooterSection";
import PricingHeader from "../pageComponents/rizAffiliate/PricingHeader";
import ScrollingBanner from "../pageComponents/global/ScrollingBanner";
import NewPricingSection from "../pageComponents/newPricing/NewPricingSection";
import TradingCompetitionPopup from "@/components/global/TradingCompetitionPopup";

export default function PageRizAffiliateJSX({ data: rawData }: { data: PageRizAffiliateSchema }) {
  const pricingAssetKey = stegaClean(rawData?.pricingSectionV2AssetKey);
  const data = withAffiliateOfferCopy(withAffiliateOfferDiscount(rawData), pricingAssetKey);

  // The VIP Deal button holds the affiliate params every checkout CTA must carry.
  const affiliateCtaUrl = stegaClean(data?.vipDealCTA?.cta?.link?.url);

  return (
    <main className="bg-white">
      <style
        jsx
        global>{`
        /* A Blacker span would otherwise grow the Geist heading's line box. */
        .richtext :is(h1, h2, h3, h4) span {
          line-height: 0;
        }
      `}</style>

      <TradingCompetitionPopup
        data={data?.tradingCompetitionPopup}
        language={data?.settings?.language}
        affCtaUrl={affiliateCtaUrl}
      />

      <HeroSection data={data} />

      <VipDealSection data={data} />

      {data?.pricingSectionV3 && (
        <NewPricingSection
          sectionV3={data.pricingSectionV3}
          section={data.pricingSectionV2}
          language={data.settings?.language}
          fixedAsset={pricingAssetKey === "futures" || pricingAssetKey === "cfd_crypto" ? pricingAssetKey : undefined}
          checkoutHrefOverride={affiliateCtaUrl}
          renderHero={(copy) => (
            <PricingHeader
              data={data}
              copy={copy}
            />
          )}
          sectionClassName="pt-[8rem] pb-[8rem] lg:pt-[17.5rem] lg:pb-[18.5rem]"
          innerClassName="w-[min(150rem,100%)] gap-[4rem] lg:gap-[5rem]"
        />
      )}

      <EdgeSection data={data} />

      <AboutSection data={data} />

      <ScrollingBanner
        className="h-[8.625rem] border-y border-black/10"
        gapRem={6}
        separatorClassName="h-[0.5rem] w-[0.5rem] rounded-none"
        wordClassName="font-geist text-[2rem] tracking-normal"
        words={data?.stepsScrollingWords?.words}
        wordsI18n={data?.stepsScrollingWords?.wordsI18n}
      />

      <StepsSection data={data} />

      {(data?.tradersSayChip || data?.tradersSayHeading || data?.tradersSayItems?.length || data?.tradersSayCTA) && (
        <TradersSaySection data={data} />
      )}

      <ComparisonSection data={data} />

      {(data?.testimonialsChip ||
        data?.testimonialsHeading ||
        data?.testimonialsRating ||
        data?.testimonialsRatingText ||
        data?.testimonialsItems?.length) && <TestimonialsSection data={data} />}

      {(data?.faqChip || data?.faqHeading || data?.faqItems?.length) && <FAQSection data={data} />}

      {(data?.finalCtaChip ||
        data?.finalCtaHeading ||
        data?.finalCtaSubheading ||
        data?.finalCtaButton ||
        data?.finalCtaImage) && <FinalCTASection data={data} />}

      {data?.settings?.footer && (
        <FooterSection
          data={data.settings.footer}
          language={data?.settings?.language}
        />
      )}
    </main>
  );
}
